import { Component, Output, EventEmitter, Input, OnInit, OnDestroy, ViewEncapsulation, ViewChild, TemplateRef } from '@angular/core';
import { ItemsSection, Field, HeaderSection, OtherInformationSection, DeliverySection, PaymentSection, FinalSection, SpecificRequirementsSection } from 'src/app/shared/models/data/documentTemplate';
import { CustomerSpecialRequirement, EnquiryHeader, EnquiryHeaderItem } from 'src/app/shared/models/data/enquiry.model';
import { Subscription } from 'rxjs';
import { BackendService } from 'src/app/shared/services/backend/backend.service';
import { TeiasoftLoaderService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-loader.service';
import { TranslateService } from '@ngx-translate/core';
import { TeiasoftToasterService } from 'src/app/shared/services/teiasoft-bucket/teiasoft-toaster.service';
import { DEFAULT_HEADER } from './document-sections/header-section/default-header';
import { QuotationConfigurationAndPdf, QuotationConfigurationTemplate, QuotationData, QuotationDataAndConfiguration } from 'src/app/shared/models/data/quotationData';
import pdfMake from 'pdfmake/build/pdfmake';
import { generatePdf, generateHeaderSection, addWatermark, generateOtherInformationSection, addFooterToAllPages, generateFinalSection, generateDeliverySection, generatePaymentSection, generateGenericItemsSection, getNumberOfPages, generateSpecificRequirementsSection } from './pdf-utils';  // Import the utility function
import { DomSanitizer } from '@angular/platform-browser';
import _ from 'lodash';
import { getHollowBarsSection, getTubesAndPipesSection, HOLLOW_BARS, TUBES_AND_PIPES } from './document-sections/generic-items-section/default-tubes-and-pipes';
import { SelectOption } from 'src/app/shared/teiasoft-bucket/teiasoft-select/teiasoft-select.component';
import { UtilsService } from 'src/app/shared/services/utils/utils.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { DatePipe } from '@angular/common';
import { Globals } from 'src/app/shared/globals/globals';
import { UserConfiguration } from 'src/app/shared/models/data/userConfiguration.model';

@Component({
  selector: 'app-document-template',
  templateUrl: './document-template.component.html',
  styleUrls: ['./document-template.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class DocumentTemplateComponent implements OnInit, OnDestroy {

  @Input() enquiryNo: string = "";
  @Input() enquiryStatus: string = "";
  @Input() isMobile: boolean = false;
  @Input() theme: string = "silver";
  @Output() closeModal: EventEmitter<any> = new EventEmitter();

  @ViewChild('confirmTemplate') confirmTemplate!: TemplateRef<any>;


  showImportModal: boolean = false;
  isPdfPreviewInvalidated: boolean = false;
  hasSpecificRequirementsSection: boolean = false;
  customerSpecialRequirements: CustomerSpecialRequirement[] = [];


  // Sections
  headerSection: HeaderSection = {
    topSection: { fields: {} },
    middleSection: { fields: {} },
    bottomSection: {
      isModified: false,
      value: ''
    },
  };
  otherInformationSection: OtherInformationSection = {
    bottomSection: {
      isModified: false,
      value: ''
    },
    sections: []
  }
  specificRequirementsSection: SpecificRequirementsSection = {
    section: {
      isModified: false,
      value: ''
    }
  };
  deliverySection: DeliverySection = {
    section: {
      isModified: false,
      value: ''
    }
  };
  paymentSection: PaymentSection = {
    section: {
      isModified: false,
      value: ''
    }
  };
  finalSection: FinalSection = {
    section: {
      isModified: false,
      value: ''
    }
  };
  itemsSection: ItemsSection[] = [];
  selectedLang: string = 'en';
  uncategorizedItems: EnquiryHeaderItem[] = [];  // Items removed from sections
  enquiry?: EnquiryHeader;

  loading: boolean = true;
  step = -1;
  subscriptions: Subscription = new Subscription();

  pdfSrc: string | null = null;
  isPreviewVisible: boolean = false;
  isGeneratingPdf: boolean = false;
  quotationData: QuotationData | undefined;
  languageOptionList: SelectOption[] = [
    {
      text: 'English',
      value: 'en'
    },
    {
      text: 'Italiano',
      value: 'it'
    },
    {
      text: 'Deutsch',
      value: 'de'
    },
    {
      text: 'Français',
      value: 'fr'
    }
  ];
  translations: any;

  constructor(
    private global: Globals,
    private teiasoftLoader: TeiasoftLoaderService,
    private backendService: BackendService,
    private translate: TranslateService,
    private teiasoftToaster: TeiasoftToasterService,
    private utilsService: UtilsService,
    private dialog: MatDialog,
    private datePipe: DatePipe,
    private sanitizer: DomSanitizer) { }

  ngOnInit(): void {
    this.loading = true;
    this.translate.getTranslation('en').subscribe((enTranslations) => {
      this.translate.store.translations['en'] = enTranslations;

      this.translate.getTranslation('fr').subscribe((frTranslations) => {
        this.translate.store.translations['fr'] = frTranslations;

        this.translate.getTranslation('it').subscribe((itTranslations) => {
          this.translate.store.translations['it'] = itTranslations;

          this.translate.getTranslation('de').subscribe((deTranslations) => {

            this.translate.store.translations['de'] = deTranslations;
            // Now we can access 'en', 'it', and 'de' translations
            this.translations = this.translate.store.translations;
            this.fetchData();

          });
        });
      });

    });
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.pdfSrc = null;
  }

  setStep(index: number) {
    this.step = index;
  }

  nextStep() {
    this.step++;
  }

  prevStep() {
    this.step--;
  }

  onSelectedLanguage($event: SelectOption) {
    this.selectedLang = $event.value;
    this.prefillDefaultValues();
    this.invalidatePdfPreview();
  }

  onHeaderChange(header: HeaderSection) {
    this.headerSection = header;
    this.headerSection.bottomSection.isModified = this.areDifferentHtmlContents(this.headerSection.bottomSection.value!, this.translations[this.selectedLang]['quotation']['headerDefault']);
    this.invalidatePdfPreview();
  }

  onOtherInformationChange(otherInformation: OtherInformationSection) {
    this.otherInformationSection = otherInformation;
    this.otherInformationSection.bottomSection.isModified = this.areDifferentHtmlContents(this.otherInformationSection.bottomSection.value!, this.translations[this.selectedLang]['quotation']['otherInformationDefault']);
    this.invalidatePdfPreview();
  }

  onSpecificRequirementsChange(section: SpecificRequirementsSection) {
    this.specificRequirementsSection = section;
    this.specificRequirementsSection.section.isModified = this.areDifferentHtmlContents(
      this.specificRequirementsSection.section.value!,
      this.buildSpecificRequirementsDefault()
    );
    this.invalidatePdfPreview();
  }

  onDeliveryChange(delivery: DeliverySection) {
    this.deliverySection = delivery;
    this.deliverySection.section.isModified = this.areDifferentHtmlContents(this.deliverySection.section.value!, this.translations[this.selectedLang]['quotation']['deliveryDefault']);
    this.invalidatePdfPreview();
  }

  onPaymentChange(payment: PaymentSection) {
    this.paymentSection = payment;
    this.paymentSection.section.isModified = this.areDifferentHtmlContents(this.paymentSection.section.value!, this.getPaymentDefaultSectionValue());
    // Check if any payment options are selected to determine if modified
    if (!this.paymentSection.section.isModified) {
      this.paymentSection.section.isModified = Object.keys(this.paymentSection.selectedValues!).length !== 0;
    }

    this.invalidatePdfPreview();
  }

  onFinalSectionChange(finalSect: FinalSection) {
    this.finalSection = finalSect;
    this.finalSection.section.isModified = this.areDifferentHtmlContents(this.finalSection.section.value!, this.translations[this.selectedLang]['quotation']['finalSectionDefault']);
    this.invalidatePdfPreview();
  }

  onSectionChange(updatedSectionData: ItemsSection, originalSection: ItemsSection) {
    const sectionIndex = this.itemsSection.findIndex(sec => sec.sectionName === originalSection.sectionName);
    if (sectionIndex > -1) {
      // Update the corresponding section in itemsSection
      this.itemsSection[sectionIndex] = updatedSectionData;
      if (this.itemsSection[sectionIndex]) {
        let section = this.itemsSection[sectionIndex];
        if (section.sectionName === TUBES_AND_PIPES) {
          const hasCustomText = this.areDifferentHtmlContents(section.technicalDescription.value!, this.translations[this.selectedLang]['quotation']['itemsTubesAndPipesDefault']);
          const hasCustomSelects = section.technicalDescription.selectedValues && Object.keys(section.technicalDescription.selectedValues).length !== 0;
          section.technicalDescription.isModified = hasCustomText || hasCustomSelects;
        }
        if (section.sectionName === HOLLOW_BARS) {
          const hasCustomText = this.areDifferentHtmlContents(section.technicalDescription.value!, this.translations[this.selectedLang]['quotation']['itemsHollowBarsDefault']);
          const hasCustomSelects = section.technicalDescription.selectedValues && Object.keys(section.technicalDescription.selectedValues).length !== 0;
          section.technicalDescription.isModified = hasCustomText || hasCustomSelects;
        }
      }

    }
    this.invalidatePdfPreview();
  }

  normalizeHtml(html: string): string {
    const txt = document.createElement("textarea");
    txt.innerHTML = html;
    const decodedHtml = txt.value;

    // Remove HTML tags, trim spaces, and normalize whitespace
    return decodedHtml
      .replace(/<\/?[^>]+(>|$)/g, "")
      .replace(/\s+/g, " ")
      .trim();
  }

  isHtmlEmpty(html?: string | null): boolean {
    if (!html) {
      return true;
    }

    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html').body;

    doc.querySelectorAll('select').forEach(select => select.remove());

    const text = (doc.textContent || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\s+/g, '')
      .trim();

    return text.length === 0;
  }

  areDifferentHtmlContents(string1: string, string2: string): boolean {
    const parser = new DOMParser();

    const extractNormalizedText = (html: string) => {
      const doc = parser.parseFromString(html, 'text/html').body;

      // Remove all <select> elements before extracting text
      doc.querySelectorAll('select').forEach(select => select.remove());

      return doc.textContent?.replace(/\s+/g, '').trim() || '';
    };

    return extractNormalizedText(string1) !== extractNormalizedText(string2);
  }



  togglePreviewVisibility() {
    this.isPreviewVisible = !this.isPreviewVisible;
    if (!this.isPreviewVisible) {
      this.pdfSrc = '';  // Clear the PDF when hiding preview
    }
  }

  getLanguages() {
    return this.utilsService.getLanguages().subscribe(res => {
      if (res) {
        this.languageOptionList = res.map(
          lang => {
            let sc = new SelectOption();
            sc.text = lang.description;
            sc.value = lang.lang;
            return sc;
          }
        )
      }
    });
  }

  getLanguageBasedOnCustomerCountry(): string {
    if (!this.enquiry)
      return 'en';
    else {
      const customerCountry = this.enquiry.customer?.customerCountry?.countryCode?.toLowerCase();
      const customerCountryLang = this.languageOptionList.find(lang => lang.value == customerCountry)
      if (customerCountryLang) {
        return customerCountryLang.value;
      }
      else
        return 'en'; // fallback to english
    }
  }

  fetchData() {
    this.teiasoftLoader.start();
    this.subscriptions.add(this.backendService.getQuotationDataAndConfiguration(this.enquiryNo).subscribe({
      next: async (res) => {
        this.teiasoftLoader.stop();

        this.quotationData = res.quotationData;
        this.enquiry = res.quotationData?.enquiry;

        // Check for customer special requirements
        // if any exist we have to show the customer special requirements section in the pdf
        this.customerSpecialRequirements = (res.quotationData?.customerSpecialRequirements ?? [])
          .map(csr => ({ ...csr }))
          .sort((a, b) => (a.rowNumber ?? 0) - (b.rowNumber ?? 0));
        this.hasSpecificRequirementsSection = this.customerSpecialRequirements.some(csr => !!csr.isCustomerRelevant);

        this.selectedLang = this.getLanguageBasedOnCustomerCountry();

        if (res.quotationConfiguration) {

          const config = JSON.parse(res.quotationConfiguration) as QuotationConfigurationTemplate;

          this.selectedLang = config.language || this.getLanguageBasedOnCustomerCountry();
          this.headerSection = config.headerSection!;
          this.itemsSection = config.itemsSection!;
          this.specificRequirementsSection = config.specificRequirementsSection || { section: { isModified: false, value: '' } };
          this.otherInformationSection = config.otherInformationSection!;
          this.deliverySection = config.deliverySection!;
          this.paymentSection = config.paymentSection!;
          this.finalSection = config.finalSection!;
          this.hasSpecificRequirementsSection = this.customerSpecialRequirements.some(csr => !!csr.isCustomerRelevant);

          const itemsMap = new Map<string, EnquiryHeaderItem>();
          (res.quotationData?.items || []).forEach(item => {
            itemsMap.set(item.itemNo!, item);
          });

          const assignedItems = new Set<string>();
          const updatedSections = await Promise.all(
            config.itemsSection!.map(section =>
              this.assignItemsToSectionsAndAddRemarks(section, itemsMap, assignedItems)
            )
          );

          this.itemsSection = updatedSections.filter((section): section is ItemsSection => section !== null);

          const unassignedItems = Array.from(itemsMap.values()).filter(item => !assignedItems.has(item.itemNo!));

          await this.categorizeItems(unassignedItems);
          this.prefillDefaultValues();

          return;
        }

        // No configuration found
        // Initialize sections with default values
        this.initSectionDefaultValues(res);
      },
      error: () => {
        this.teiasoftLoader.stop();
        this.closeModal.emit();  // Trigger the modal close and destroy the component
      },
      complete: () => { this.loading = false; }
    }));
  }

  async initSectionDefaultValues(res: QuotationDataAndConfiguration) {
    // Categorize items
    await this.categorizeItems(res.quotationData?.items || []);

    this.headerSection = JSON.parse(JSON.stringify(DEFAULT_HEADER));
    // this.otherInformationSection = JSON.parse(JSON.stringify(DEFAULT_OTHER_INFO)); // MP: REMOVED as customers don't need it
    this.prefillDefaultValues();
    this.applyUserPreferences();
  }

  applyUserPreferences(): void {
    // look for saved items sections configuration 
    let _temp: UserConfiguration = new UserConfiguration();
    if (this.global.userLogged!.configuration != '') {
      _temp = JSON.parse(this.global.userLogged!.configuration!);
      if (_temp.quotationTemplate) {
        _temp.quotationTemplate.itemSections.forEach(async userItemSection => {
          let itemSection = this.itemsSection.find(i => i.sectionName === userItemSection.sectionName);

          if (itemSection) {

            itemSection.fields = userItemSection.fields;
            itemSection.showTotalLength = userItemSection.showTotalLength;
            itemSection.showTotalPrice = userItemSection.showTotalPrice;
            itemSection.showTotalWeight = userItemSection.showTotalWeight;

            const defaultFields = await this.getDefaultFieldsForSection(itemSection.sectionName);
            itemSection.fields = this.mergeFieldsWithDefaults(itemSection.fields, defaultFields);
          }
        });
      }
    }
  }

  saveUserPreferences(): void {
    let _temp: UserConfiguration = new UserConfiguration();
    _temp = JSON.parse(this.global.userLogged!.configuration!);
    if (!_temp.quotationTemplate) {
      _temp.quotationTemplate = {
        itemSections: []
      }
    }
    if (!_temp.quotationTemplate.itemSections)
      _temp.quotationTemplate.itemSections = new Array();

    this.itemsSection.forEach(is => {
      let userItemSection = _temp.quotationTemplate?.itemSections.find(i => i.sectionName === is.sectionName);
      if (userItemSection) {
        userItemSection.fields = is.fields;
        userItemSection.showTotalLength = is.showTotalLength;
        userItemSection.showTotalPrice = is.showTotalPrice;
        userItemSection.showTotalWeight = is.showTotalWeight;
      }
      else {
        _temp.quotationTemplate?.itemSections.push(is);
      }

    })

    this.global.userLogged!.configuration = JSON.stringify(_temp);

    this.subscriptions.add(this.backendService.updateUserConfiguration(this.global.userLogged!.configuration!, this.global.userLogged!.language!, this.global.userLogged!.dateFormat!).subscribe(res => {
      localStorage.setItem("userLogged", JSON.stringify(this.global.userLogged!));
    }));
  }

  async assignItemsToSectionsAndAddRemarks(
    section: ItemsSection,
    itemsMap: Map<string, EnquiryHeaderItem>,
    assignedItems: Set<string>
  ): Promise<ItemsSection | null> {
    const defaultFields = await this.getDefaultFieldsForSection(section.sectionName);
    section.fields = this.mergeFieldsWithDefaults(section.fields, defaultFields);

    let hasAssignedItems = false;

    section.items = section.items.map(configItem => {
      const dataItem = itemsMap.get(configItem.itemNo!);

      if (dataItem) {
        const mergedItem = { ...dataItem };
        mergedItem.remark = configItem['remarks'] || '';
        mergedItem.deliveryDate = configItem['deliveryDate'] || undefined;
        assignedItems.add(configItem.itemNo!);
        hasAssignedItems = true;
        return mergedItem;
      }

      return null;
    }).filter(item => item !== null) as EnquiryHeaderItem[];

    const isBuiltInSection = section.sectionName === TUBES_AND_PIPES
      || section.sectionName === HOLLOW_BARS;

    // Built-in sections act as the saved template for newly categorized items. They
    // are removed later by categorizeItems only if they are still empty after the
    // current enquiry items have been assigned.
    return hasAssignedItems || isBuiltInSection ? section : null;
  }

  mergeFieldsWithDefaults(backendFields: Field[], defaultFields: Field[]): Field[] {
    // Map backend fields by label for quick lookup and keep their original order
    const backendFieldMap = new Map(backendFields.map(field => [field.label, field]));

    // 1) Start from default order, merging backend values when present
    const orderedMergedFields = defaultFields.map(defaultField => {
      const backendField = backendFieldMap.get(defaultField.label);
      return backendField ? { ...defaultField, ...backendField } : defaultField;
    });

    // 2) Append any backend-only fields that are not in defaults, preserving backend order
    backendFields.forEach(backendField => {
      if (!defaultFields.some(defaultField => defaultField.label === backendField.label)) {
        orderedMergedFields.push(backendField);
      }
    });

    return orderedMergedFields;
  }

  async getDefaultFieldsForSection(sectionName: string): Promise<Field[]> {
    if (sectionName === HOLLOW_BARS) {
      return (await getHollowBarsSection(this.quotationData?.otherInfo?.salesOfficeCountry!)).fields;
    }
    else
      return (await getTubesAndPipesSection(this.quotationData?.otherInfo?.salesOfficeCountry!)).fields;
  }


  async categorizeItems(items: EnquiryHeaderItem[]): Promise<void> {

    const defaultHollow = _.cloneDeepWith(await getHollowBarsSection(this.quotationData?.otherInfo?.salesOfficeCountry!), (value) => {
      // Customizer to preserve functions
      if (typeof value === 'function') {
        return value;
      }
    });

    const defaultPipes = _.cloneDeepWith(await getTubesAndPipesSection(this.quotationData?.otherInfo?.salesOfficeCountry!), (value) => {
      // Customizer to preserve functions
      if (typeof value === 'function') {
        return value;
      }
    });

    const hollowBarsSection: ItemsSection = {
      ...defaultHollow,
      sectionName: HOLLOW_BARS,
      items: []
    };

    const tubesAndPipesSection: ItemsSection = {
      ...defaultPipes,
      sectionName: TUBES_AND_PIPES,
      items: []
    };

    // Iterate over the items and categorize them
    items.forEach(item => {
      if (item.sms?.description?.includes("HOLLOW BARS")) {
        hollowBarsSection.items.push(item);
      } else {
        // Add all other items to the Tubes and Pipes section
        tubesAndPipesSection.items.push(item);
      }
    });

    if (tubesAndPipesSection.items.length > 0) {
      let sec = this.itemsSection.find(section => section.sectionName === TUBES_AND_PIPES);
      if (sec)
        sec.items = sec.items.concat(tubesAndPipesSection.items);
      else
        this.itemsSection.push(tubesAndPipesSection);
    }
    if (hollowBarsSection.items.length > 0) {
      let sec = this.itemsSection.find(section => section.sectionName === HOLLOW_BARS);
      if (sec)
        sec.items = sec.items.concat(hollowBarsSection.items);
      else
        this.itemsSection.push(hollowBarsSection);
    }

    // Finally if one of the section among Tubes and Pipes and Hollow Bars is empty we can remove it
    // all the sections with different name are kept as is
    this.itemsSection = this.itemsSection.filter(
      is => {
        if (is.sectionName != HOLLOW_BARS && is.sectionName != TUBES_AND_PIPES)
          return true;
        else {
          return is.items.length > 0;
        }
      });
  }

  addItemFromUncategorizedToSection(section: ItemsSection, item: EnquiryHeaderItem) {
    this.addItemToSection(section, item);
  }


  // User removes an item from a section
  removeItemFromSection(section: ItemsSection, item: EnquiryHeaderItem): void {
    section.items = section.items.filter(i => i.itemNo !== item.itemNo);
    this.uncategorizedItems.push(item);
  }

  // User adds an uncategorized item to a section
  addItemToSection(section: ItemsSection, item: EnquiryHeaderItem): void {
    section.items.push(item);
    this.uncategorizedItems = this.uncategorizedItems.filter(i => i.itemNo !== item.itemNo);
  }

  // Validation before save
  validateBeforeSave(): boolean {

    const allItems: EnquiryHeaderItem[] = [
      ...this.itemsSection.flatMap(section => section.items)
    ];

    const allItemsAssigned = this.uncategorizedItems.length === 0 && allItems.length === new Set(allItems).size;

    if (!allItemsAssigned) {
      this.teiasoftToaster.show("error", this.translate.instant("errors.unassignedItems"));
      return false;
    }

    if (allItemsAssigned && this.itemsSection.some(is => is.items.length === 0)) {
      this.teiasoftToaster.show("error", this.translate.instant("errors.emptyItemsSection"));
      return false;
    }

    if (!this.validateHeader()) {
      this.teiasoftToaster.show("error", this.translate.instant("errors.invalidQuotationHeader"));
      return false;
    }

    if (!this.validateOtherInformation()) {
      this.teiasoftToaster.show("error", this.translate.instant("errors.invalidQuotationOtherInformation"));
      return false;
    }

    if (!this.validateDelivery()) {
      this.teiasoftToaster.show("error", this.translate.instant("errors.invalidQuotationDelivery"));
      return false;
    }

    if (!this.validatePayment()) {
      this.teiasoftToaster.show("error", this.translate.instant("errors.invalidQuotationPayment"));
      return false;
    }

    return true;
  }

  async onSaveClick(isFinal: boolean) {
    if (isFinal && this.isEditQuotationFlow()) {
      return;
    }

    if (!this.validateBeforeSave()) {
      return;
    }

    this.teiasoftLoader.start();
    const pdf = await this.generatePdfContent(isFinal);

    pdf.getBase64((pdfBase64: string) => {
      this.saveQuotationConfiguration(pdfBase64, isFinal);
      this.teiasoftLoader.stop();
    });
  }

  saveQuotationConfiguration(pdfBase64: string, isFinal: boolean) {
    const quotationConfiguration = {
      language: this.selectedLang,
      headerSection: this.headerSection,
      specificRequirementsSection: this.specificRequirementsSection,
      otherInformationSection: this.otherInformationSection,
      deliverySection: this.deliverySection,
      paymentSection: this.paymentSection,
      itemsSection: this.itemsSection.map(s => this.extractItemNo(s)),
      finalSection: this.finalSection
    };

    const payload = new QuotationConfigurationAndPdf();
    payload.isFinal = isFinal;
    payload.quotationPdf = pdfBase64;
    payload.quotationConfiguration = JSON.stringify(quotationConfiguration);

    this.subscriptions.add(this.backendService.saveQuotationConfigurationAndPdf(this.enquiryNo, payload).subscribe({
      next: () => {
        this.teiasoftToaster.show("success", this.translate.instant(isFinal ? 'quotation.savedAsFinal' : 'quotation.savedAsDraft'));
        this.onCloseClick();
      },
      error: () => {
        this.teiasoftToaster.show("error", this.translate.instant('errors.failedToSaveQuotation'));
      },
      complete: () => {
        this.saveUserPreferences();
      }
    }));
  }

  onCloseClick() {
    this.closeModal.emit('');
  }

  isEditQuotationFlow(): boolean {
    return (this.enquiryStatus || '').toLowerCase() === 'edit'
      || (this.enquiry?.offerStatus || '').toLowerCase() === 'edit';
  }

  prefillDefaultValues() {
    this.prefillHeaderValues(this.quotationData, this.headerSection!);
    this.prefillItemsSection();
    this.prefillSpecificRequirements();
    this.prefillDeliverySection();
    this.prefillPaymentSection();
    this.prefillOtherInformation();
    this.prefillFinalSection();
  }

  prefillItemsSection() {
    this.itemsSection.forEach(is => {
      if (!is.technicalDescription.isModified) {
        if (is.sectionName === TUBES_AND_PIPES) {

          is.technicalDescription.value = this.translations[this.selectedLang]['quotation']['itemsTubesAndPipesDefault'];
        }
        if (is.sectionName === HOLLOW_BARS) {
          is.technicalDescription.value = this.translations[this.selectedLang]['quotation']['itemsHollowBarsDefault']
        }
      }
    })
  }

  prefillDeliverySection() {
    if (!this.deliverySection.section.isModified) {
      this.deliverySection.section.value = this.translations[this.selectedLang]['quotation']['deliveryDefault'];
    }
  }

  prefillPaymentSection() {
    if (!this.paymentSection.section.isModified) {
      this.paymentSection.section.value = this.getPaymentDefaultSectionValue();
      this.paymentSection.selectedValues = {};
    }
  }

  prefillSpecificRequirements() {
    if (!this.specificRequirementsSection.section.isModified) {
      this.specificRequirementsSection.section.value = this.buildSpecificRequirementsDefault();
    }
  }

  prefillOtherInformation() {
    if (!this.otherInformationSection.bottomSection.isModified) {
      this.otherInformationSection.bottomSection.value = this.translations[this.selectedLang]['quotation']['otherInformationDefault'];
    }
  }

  prefillFinalSection() {
    if (!this.finalSection.section.isModified) {
      this.finalSection.section.value = this.translations[this.selectedLang]['quotation']['finalSectionDefault'];
    }
  }

  buildSpecificRequirementsDefault(): string {
    const relevant = (this.customerSpecialRequirements ?? []).filter(csr => !!csr.isCustomerRelevant);
    const lines = relevant.map(csr => `${csr.salesPersonComment ?? ''} : ${csr.millComment ?? ''}`);
    if (relevant.length > 0 && this.quotationData?.hasDeviationList) {
      lines.push(this.translations[this.selectedLang]['quotation']['referToDeviationListAttached']);
    }
    if (lines.length === 0) {
      return '';
    }
    return lines.map(line => `<p>${line}</p>`).join('');
  }

  prefillHeaderValues(data: QuotationData | undefined, headerSection: HeaderSection) {
    if (!data || !data.enquiry)
      return;

    const fields = { ...headerSection.topSection.fields, ...headerSection.middleSection.fields };

    if (!fields['from']?.isModified && this.headerSection?.topSection?.fields?.['from']) {
      this.headerSection.topSection.fields['from'].value = data.otherInfo?.salespersonNameAndSurname || '';
    }
    if (!fields['company']?.isModified && this.headerSection?.topSection?.fields?.['company']) {
      this.headerSection.topSection.fields['company'].value = data.enquiry.customer?.customerName || '';
    }
    if (!fields['phone']?.isModified && this.headerSection?.topSection?.fields?.['phone']) {
      this.headerSection.topSection.fields['phone'].value = data.otherInfo?.salespersonPhone || '';
    }
    if (!fields['date']?.isModified && this.headerSection?.topSection?.fields?.['date']) {
      this.headerSection.topSection.fields['date'].value = new Date();
    }
    if (!fields['email']?.isModified && this.headerSection?.topSection?.fields?.['email']) {
      this.headerSection.topSection.fields['email'].value = data.otherInfo?.salespersonMail || '';
    }
    if (!fields['your_ref']?.isModified && this.headerSection?.middleSection?.fields?.['your_ref']) {
      this.headerSection.middleSection.fields['your_ref'].value = data.enquiry.customerEnquiryNo || '';
    }
    if (!fields['our_offer_no']?.isModified && this.headerSection?.middleSection?.fields?.['our_offer_no']) {
      this.headerSection.middleSection.fields['our_offer_no'].value = data.enquiry.quotationEnquiryNo || '';
    }
    if (!fields['destination']?.isModified && this.headerSection?.middleSection?.fields?.['destination']) {
      this.headerSection.middleSection.fields['destination'].value = data.enquiry.customer?.customerCountry?.countryName || '';
    }

    if (!headerSection.bottomSection?.isModified && this.headerSection?.bottomSection) {
      this.headerSection.bottomSection.isModified = false;
      this.headerSection.bottomSection.value = this.translations[this.selectedLang]['quotation']['headerDefault'];
    }
  }

  validateHeader(): boolean {
    if (!this.headerSection) {
      return false;
    }

    const isFieldValid = (field: Field) => {
      if (field.required && !field.hidden && !field.hideable) {
        return field.value !== null && field.value !== undefined && field.value !== '';
      }
      return true;
    };

    const topSectionFields = Object.values(this.headerSection.topSection.fields);
    for (const field of topSectionFields) {
      if (!isFieldValid(field)) {
        return false;
      }
    }

    const middleSectionFields = Object.values(this.headerSection.middleSection.fields);
    for (const field of middleSectionFields) {
      if (!isFieldValid(field)) {
        return false;
      }
    }

    return true;
  }

  validateOtherInformation(): boolean {
    if (this.otherInformationSection.sections.length === 0) {
      return true;
    }

    return this.otherInformationSection.sections.every(section => {
      return section.title.trim() !== '' && section.value.trim() !== '';
    });
  }

  validateDelivery(): boolean {
    return this.deliverySection.section.value?.trim() !== '';
  }

  validatePayment(): boolean {
    return this.paymentSection.section.value?.trim() !== '';
  }


  extractItemNo(section: ItemsSection): Partial<ItemsSection> {
    const { items, ...sectionWithoutItems } = section;
    const itemsWithItemNoOnly = items.map(item => ({ itemNo: item.itemNo, remarks: item.remark, deliveryDate: item.deliveryDate }));
    return { ...sectionWithoutItems, items: itemsWithItemNoOnly };
  }

  async generatePdfPreview() {
    this.setStep(-1);
    if (!this.validateBeforeSave()) {
      return;
    }

    this.isGeneratingPdf = true;
    this.isPdfPreviewInvalidated = false;  // Reset the flag after PDF is generated

    const pdf = await this.generatePdfContent();

    pdf.getBlob((pdfBlob: Blob) => {
      this.displayPdfInViewer(pdfBlob);
      this.isGeneratingPdf = false;
      this.isPreviewVisible = true;
    });

  }

  getFormattedDate(date?: number): string {
    if (!date)
      return '';
    const dateFormat = this.enquiry?.customer?.customerCountry?.countryCode?.toLowerCase() == 'us' ? 'MM/dd/yyyy' : 'dd/MM/yyyy';

    const formattedDate = this.datePipe.transform(date, dateFormat);
    return formattedDate || '';
  }


  getMaxDeliveryDate(): string {
    const maxWeeks = this.itemsSection
      .flatMap(section => section.items) // Flatten all items
      .flatMap(item => item.deliveryDatesCollection || []) // Flatten all delivery dates
      .map(dd => dd.deliveryNumberOfWeeksWithExtraLeadTime)
      .filter(weeks => typeof weeks === 'number' && weeks > 0)
      .reduce((max, current) => Math.max(max!, current!), 0);

    return maxWeeks === 0 ? '' : this.formatWeeks(maxWeeks!);
  }

  formatWeeks(weeks: number): string {
    const lang = (this.selectedLang || 'en').toLowerCase();
    const forms: Record<string, { one: string; other: string }> = {
      en: { one: 'week', other: 'weeks' },
      it: { one: 'settimana', other: 'settimane' },
      fr: { one: 'semaine', other: 'semaines' },
      de: { one: 'Woche', other: 'Wochen' },
    };
    const pr = new Intl.PluralRules(lang);
    const rule = pr.select(weeks);
    const word = (forms[lang] ?? forms['en'])[(rule === 'one' ? 'one' : 'other')];
    return `${weeks} ${word}`;
  }


  async generatePdfContent(isFinal: boolean = false) {
    const offerValidityDate = this.getFormattedDate(this.quotationData?.otherInfo?.offerValidityDate) || '';
    const deliveryDate = this.getMaxDeliveryDate();
    const docDefinition = await this.buildDocDefinition(offerValidityDate, deliveryDate, isFinal);

    // First PDF creation to get the number of pages
    const document = pdfMake.createPdf(docDefinition);
    const pagesCount = await getNumberOfPages(document);

    // Update the header with the number of pages
    this.headerSection!.topSection.fields['pages'].value = pagesCount;

    // Regenerate the document with updated page count
    const finalDocDefinition = await this.buildDocDefinition(offerValidityDate, deliveryDate, isFinal);

    // Return the final document
    return pdfMake.createPdf(finalDocDefinition);
  }

  private async buildDocDefinition(offerValidityDate: string, deliveryDate: string, isFinal: boolean) {
    const docDefinition = generatePdf(this.translations[this.selectedLang], undefined, this.headerSection.middleSection.fields['your_ref'].value, this.enquiry!.quotationEnquiryNo!);

    docDefinition.content.push(...generateHeaderSection(this.headerSection!, this.translations[this.selectedLang], this.getFormattedDate.bind(this)));
    this.addEmptyParagraph(docDefinition);

    const sectionsWithItems = this.itemsSection.filter(section => section.items.length > 0);
    for (const section of sectionsWithItems) {
      docDefinition.content.push(...await generateGenericItemsSection(section, this.translations[this.selectedLang], this.formatWeeks.bind(this), this.enquiry?.customer?.customerCountry?.culture, this.selectedLang));
    }

    if (this.hasSpecificRequirementsSection && !this.isHtmlEmpty(this.specificRequirementsSection.section.value)) {
      // this.addEmptyParagraph(docDefinition);
      docDefinition.content.push(...generateSpecificRequirementsSection(this.specificRequirementsSection, this.translations[this.selectedLang]));
    }

    // this.addEmptyParagraph(docDefinition);
    docDefinition.content.push(...await generateOtherInformationSection(this.otherInformationSection!));
    this.addEmptyParagraph(docDefinition);
    docDefinition.content.push(...await generateDeliverySection(this.deliverySection!, offerValidityDate, deliveryDate));
    this.addEmptyParagraph(docDefinition);
    docDefinition.content.push(...await generatePaymentSection(this.paymentSection!, this.translations[this.selectedLang]));
    this.addEmptyParagraph(docDefinition);
    docDefinition.content.push(...generateFinalSection(this.finalSection, this.headerSection?.topSection.fields['from'].value, this.enquiry?.salesOffice!));
    addFooterToAllPages(docDefinition, this.enquiry?.salesOffice!);

    if (!isFinal) {
      addWatermark(docDefinition, "DRAFT");
    }

    const pdfStyles = {
      'html-p': {
        margin: [0, 0, 0, 0],  // sovrascrive per tutti gli html-p
      },
    };

    docDefinition.styles = pdfStyles;
    docDefinition.content = this.overrideParagraphMargins(docDefinition.content);

    return docDefinition;
  }

  addEmptyParagraph(docDefinition: any): void {
    docDefinition.content.push({ text: ' ' });
  }

  overrideParagraphMargins(content: any): any {
    if (Array.isArray(content)) {
      return content.map(item => this.overrideParagraphMargins(item));
    }

    if (typeof content === 'object' && content !== null) {
      const updated = { ...content };

      // Se è un paragrafo html-p con margine, lo sovrascriviamo
      if (
        (updated.nodeName === 'P' || (updated.style && updated.style.includes('html-p'))) &&
        updated.margin
      ) {
        updated.margin = [0, 5, 0, 0];
      }

      // Ricorsione su stack
      if (updated.stack) {
        updated.stack = this.overrideParagraphMargins(updated.stack);
      }

      // Ricorsione su columns (se presenti)
      if (updated.columns) {
        updated.columns = this.overrideParagraphMargins(updated.columns);
      }

      // Ricorsione su table.body
      if (updated.table && updated.table.body) {
        updated.table.body = updated.table.body.map((row: any[]) =>
          row.map(cell => this.overrideParagraphMargins(cell))
        );
      }

      return updated;
    }

    // Default: ritorna come è (es. stringhe, numeri)
    return content;
  }


  displayPdfInViewer(pdfBlob: Blob) {
    const pdfUrl = URL.createObjectURL(pdfBlob);
    this.pdfSrc = this.sanitizer.bypassSecurityTrustResourceUrl(pdfUrl) as string;
  }


  openImportModal() {
    this.showImportModal = true;
  }

  onCloseImportModal($event: Event) {
    this.showImportModal = false;
  }

  async onImportConfiguration($event: Event) {
    const importedConfig = $event as QuotationConfigurationTemplate;
    await this.importExistingConfiguration(importedConfig);

  }

  async importExistingConfiguration(config: QuotationConfigurationTemplate) {
    if (!config) {
      //  cannot import
      return;
    }

    if (config.headerSection) {
      this.headerSection = config.headerSection;
      this.prefillHeaderValues(this.quotationData, this.headerSection!)
    }
    if (config.itemsSection) {
      this.itemsSection = new Array();
      config.itemsSection.forEach(async (importedSection, index) => {
        importedSection.items = [];
        const existingSection = this.itemsSection.find(sec => sec.sectionName === importedSection.sectionName);

        if (existingSection) {
          existingSection.technicalDescription = importedSection.technicalDescription;
          existingSection.freeText = importedSection.freeText;
          existingSection.fields = this.mergeFieldsWithRender(existingSection.fields, importedSection.fields);
        } else {
          // If the section does not exist, merge in default field renderers before pushing
          const defaultFields = await this.getDefaultFieldsForSection(importedSection.sectionName);
          importedSection.fields = this.mergeFieldsWithDefaults(importedSection.fields, defaultFields);
          this.itemsSection.push(importedSection);
        }
      });
      this.categorizeItems(this.quotationData?.items || []);
    }
    if (config.otherInformationSection) {
      this.otherInformationSection = config.otherInformationSection;
    }
    if (config.specificRequirementsSection) {
      this.specificRequirementsSection = config.specificRequirementsSection;
    }
    if (config.deliverySection) {
      this.deliverySection = config.deliverySection;
    }
    if (config.paymentSection) {
      this.paymentSection = config.paymentSection;
    }

  }

  mergeFieldsWithRender(existingFields: Field[], importedFields: Field[]): Field[] {
    const existingFieldMap = new Map(existingFields.map(field => [field.label, field]));

    return importedFields.map(importedField => {
      const existingField = existingFieldMap.get(importedField.label);
      if (existingField) {
        // Preserve the render property from the existing field
        return { ...importedField, render: importedField.render, renderCustomLabel: existingField.renderCustomLabel };
      }
      return importedField; // If no existing field, use imported field as is
    });
  }

  isReadyToGeneratePdf(): boolean {
    return !!this.headerSection && !!this.otherInformationSection && !!this.deliverySection && !!this.paymentSection && this.itemsSection.length > 0;
  }

  invalidatePdfPreview() {
    this.isPdfPreviewInvalidated = true;
  }

  // Method to reset the free text section
  resetSection(section: any, defaultValue: string, resetSelectedValues: boolean = false, onReset?: () => void): void {
    const dialogRef: MatDialogRef<any> = this.dialog.open(this.confirmTemplate, {
      data: {} // No data is passed here
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        section.isModified = false;
        section.value = defaultValue;

        if (resetSelectedValues && section.selectedValues) {
          section.selectedValues = {};
        }
        if (onReset) {
          onReset();
        }

        this.invalidatePdfPreview(); // Invalidate the preview if needed
      }
    });
  }

  resetHeaderSection(): void {
    this.resetSection(this.headerSection.bottomSection, this.translations[this.selectedLang]['quotation']['headerDefault']);
  }

  resetTechnicalDescription(sectionName: string): void {
    if (sectionName === TUBES_AND_PIPES) {
      let sectionIndex = this.itemsSection.findIndex(is => is.sectionName == TUBES_AND_PIPES);
      if (sectionIndex !== -1) {
        let section = { ...this.itemsSection[sectionIndex] };
        this.resetSection(section.technicalDescription, this.translations[this.selectedLang]['quotation']['itemsTubesAndPipesDefault'], true);
        this.itemsSection[sectionIndex] = section;
      }
    }

    if (sectionName === HOLLOW_BARS) {
      let sectionIndex = this.itemsSection.findIndex(is => is.sectionName == HOLLOW_BARS);
      if (sectionIndex !== -1) {
        let section = { ...this.itemsSection[sectionIndex] };
        this.resetSection(section.technicalDescription, this.translations[this.selectedLang]['quotation']['itemsHollowBarsDefault'], true);
        this.itemsSection[sectionIndex] = section;
      }
    }
  }


  resetOtherInformationSection(): void {
    this.resetSection(this.otherInformationSection.bottomSection, this.translations[this.selectedLang]['quotation']['otherInformationDefault']);
  }

  resetSpecificRequirementsSection(): void {
    this.resetSection(this.specificRequirementsSection.section, this.buildSpecificRequirementsDefault());
  }

  resetDeliverySection(): void {
    this.resetSection(this.deliverySection.section, this.translations[this.selectedLang]['quotation']['deliveryDefault']);
  }

  resetPaymentSection(): void {
    this.resetSection(this.paymentSection.section, this.getPaymentDefaultSectionValue(), true, () => {
      this.paymentSection.selectedValues = {};
    });
  }

  resetFinalSection(): void {
    this.resetSection(this.finalSection.section, this.translations[this.selectedLang]['quotation']['finalSectionDefault']);
  }

  getPaymentDefaultSectionValue(): string {
    return this.translations[this.selectedLang]['quotation']['paymentDefault'][this.enquiry!.salesOffice!] + this.translations[this.selectedLang]['quotation']['paymentsTermsDefault'];
  }

}
